// Menü öğe ağacı yardımcıları — reorder payload üretimi ve sürükle-bırak
// (re-parent destekli) yeniden yapılandırma. Saf fonksiyonlar; girdiyi
// mutasyona uğratmaz (önce derin kopyalar).

import type { MenuItemNode, MenuReorderNode } from '../types';

/** Ağacı derin kopyalar (girdi immutable kalır). */
function cloneTree(nodes: MenuItemNode[]): MenuItemNode[] {
  return nodes.map((n) => ({ ...n, children: cloneTree(n.children) }));
}

/**
 * DFS: her düğüme kardeşleri içindeki 0-tabanlı sırayı ve parentId'sini atar.
 * BE reorder ucunun beklediği düz `{ id, order, parentId }` listesini üretir.
 */
export function flattenForReorder(items: MenuItemNode[]): MenuReorderNode[] {
  const out: MenuReorderNode[] = [];
  const walk = (nodes: MenuItemNode[], parentId: string | null) => {
    nodes.forEach((n, idx) => {
      out.push({ id: n.id, order: idx, parentId });
      if (n.children.length) walk(n.children, n.id);
    });
  };
  walk(items, null);
  return out;
}

export interface TreeDropInfo {
  dragKey: string;
  dropKey: string;
  /** AntD göreli konum: <0 hedefin üstü, 0 hedefin içi, >0 hedefin altı. */
  dropPosition: number;
  /** true → kardeş boşluğuna (üst/alt); false → hedefin içine (çocuk). */
  dropToGap: boolean;
}

/**
 * Sürükle-bırak sonucunu yeni bir ağaca uygular. `dropToGap=false` ise
 * sürüklenen düğüm hedefin çocuğu olur (re-parent); aksi halde hedefin
 * kardeşi olarak üstüne/altına yerleşir. Döngü/geçersiz taşımalar BE
 * tarafından reddedilir (MENU_CYCLE / PARENT_MENU_MISMATCH).
 */
export function applyTreeDrop(items: MenuItemNode[], info: TreeDropInfo): MenuItemNode[] {
  const data = cloneTree(items);

  let dragObj: MenuItemNode | undefined;
  const remove = (nodes: MenuItemNode[]): MenuItemNode[] =>
    nodes.filter((n) => {
      if (n.id === info.dragKey) {
        dragObj = n;
        return false;
      }
      n.children = remove(n.children);
      return true;
    });
  const next = remove(data);
  if (!dragObj) return items;

  // Hedef, sürüklenenin kendi alt ağacındaysa taşıma geçersiz — girdiyi koru.
  if (containsId(dragObj, info.dropKey)) return items;

  if (!info.dropToGap) {
    const inserted = insertAsChild(next, info.dropKey, dragObj);
    if (!inserted) return items;
  } else {
    const inserted = insertAsSibling(next, info.dropKey, dragObj, info.dropPosition);
    if (!inserted) next.push(dragObj);
  }
  return next;
}

function containsId(node: MenuItemNode, id: string): boolean {
  if (node.id === id) return true;
  return node.children.some((c) => containsId(c, id));
}

function insertAsChild(nodes: MenuItemNode[], dropKey: string, dragObj: MenuItemNode): boolean {
  for (const n of nodes) {
    if (n.id === dropKey) {
      n.children = [...n.children, dragObj];
      return true;
    }
    if (insertAsChild(n.children, dropKey, dragObj)) return true;
  }
  return false;
}

function insertAsSibling(
  nodes: MenuItemNode[],
  dropKey: string,
  dragObj: MenuItemNode,
  dropPosition: number,
): boolean {
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i]?.id === dropKey) {
      const at = dropPosition < 0 ? i : i + 1;
      nodes.splice(at, 0, dragObj);
      return true;
    }
    if (insertAsSibling(nodes[i]?.children ?? [], dropKey, dragObj, dropPosition)) return true;
  }
  return false;
}
